'use client';

import type { ReactNode } from 'react';

import { AnimatePresence, motion, useReducedMotion, type Transition } from 'motion/react';

import { UI_EASE } from '@/components/reveal';
import { cn } from '@/lib/utils';

/** One straight pass. A spring overshoots and a reel does not bounce. */
const ROLL: Transition = { duration: 0.45, ease: UI_EASE };

/**
 * Rolls one line out of view and the next up into its place, like a reel — the whole line moves
 * as one piece, not letter by letter.
 *
 * Both lines are in the same grid cell, and AnimatePresence keeps the outgoing one mounted while
 * the incoming one arrives, so they travel together: the old is still climbing out of the top as
 * the new comes up past the bottom. `mode="wait"` would play them one after the other, which
 * reads as two separate moves rather than one reel turning.
 *
 * `leading` sits inside the moving line, so an icon belonging to the text rolls with it instead
 * of standing still while the words change underneath.
 */
export function RotatingText({
  text,
  leading,
  className,
}: {
  text: string;
  leading?: ReactNode;
  className?: string;
}) {
  const reducedMotion = useReducedMotion() ?? false;

  if (reducedMotion) {
    return (
      <span className={cn('inline-flex items-center gap-1.5', className)}>
        {leading}
        {text}
      </span>
    );
  }

  return (
    <motion.span
      layout
      transition={ROLL}
      // The line slides through this box, so it has to clip. layout carries the width between
      // two strings of different lengths — without it the box snaps and the roll reads as a jump.
      className={cn('relative inline-grid overflow-hidden', className)}
    >
      <span className="sr-only">{text}</span>
      {/* popLayout, not the default: it lifts the outgoing line out of flow, so the box is sized
          by the incoming one alone. Left in flow, the grid cell holds whichever line is wider for
          the length of the roll and then snaps — which is the gap that opens next to the icon on
          a shorter domain and jumps shut when the old line unmounts. Out of flow, `layout` above
          animates the width from the old to the new instead. */}
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={text}
          aria-hidden="true"
          initial={{ y: '100%' }}
          animate={{ y: '0%' }}
          exit={{ y: '-100%' }}
          transition={ROLL}
          // Same cell for every line, so they stack and pass each other rather than sitting
          // side by side while both are mounted.
          className="col-start-1 row-start-1 inline-flex items-center gap-1.5 whitespace-nowrap"
        >
          {leading}
          {text}
        </motion.span>
      </AnimatePresence>
    </motion.span>
  );
}
